export enum ResizableImageFormats {
  JPEG = 'jpeg',
  PNG = 'png',
  GIF = 'gif',
  WEBP = 'webp',
  AVIF = 'avif',
}

type QualityRange = 70 | 75 | 80 | 85 | 90 | 95 | 100

export interface GetResizedImageUrlParams {
  width?: number
  height?: number
  quality?: QualityRange
  format?: ResizableImageFormats
}

/**
 * url을 기반으로 힐링페이퍼 리사이저 스팩에 따라 이미지를 리사이징 합니다.
 * @docs https://www.notion.so/healingpaper/79fdf01eaae24f98908493a1058fe0d8?pvs=4#930adf0b125343e9bec64c144e501f68
 * @param {string} url 리사이징 할 이미지 URL
 * @param {GetResizedImageUrlParams} params 리사이징 옵션
 * @param {number} params.width 리사이징 할 이미지의 너비, height가 없을 경우 비율에 맞춰 자동 조정
 * @param {number} params.height 리사이징 할 이미지의 높이
 * @param {QualityRange} params.quality 이미지의 품질, 0 ~ 100 사이의 값. 입력하지 않을 경우 서버에서 75로 처리, 편의와 최소 품질 보장을 위해 70부터 5단위로 타입을 정의.
 * @param {ResizableImageFormats} params.format
 * @returns {string} 리사이징 된 이미지 URL
 */
export const getResizedImageUrl = (
  url: string,
  { width, height, quality, format }: GetResizedImageUrlParams = {},
): string => {
  if (!validateImageUrl(url)) {
    return url
  }
  const [baseUrl] = url.split('?')
  const params = new URLSearchParams()
  if (width) params.set('w', width.toString())
  if (height) params.set('h', height.toString())
  if (quality) params.set('q', quality.toString())
  if (format) params.set('f', format)

  const queryParameters = params.toString()
  return `${baseUrl}${queryParameters ? `?${queryParameters}` : ''}`
}

const validateImageUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url)
    const validHosts = ['image2.gnsister.com', 'image.test.gnsister.com']
    return validHosts.includes(parsedUrl.host)
  } catch {
    return false
  }
}

/**
 * html에서 img 태그의 src 값을 converter의 로직으로 수정합니다.
 * @param {string} html HTML 문자열
 * @param {(url: string) => string} converter src 값을 수정할 로직
 * @returns {string} src 값이 수정된 HTML 문자열
 */
export function updateImgSrcUrls(html: string, converter: (url: string) => string): string {
  const imgSrcRegex = /<img[^>]+src="([^"]+)"/g

  return html.replace(imgSrcRegex, (match, url) => {
    const newUrl = converter(url)
    return match.replace(url, newUrl)
  })
}
